import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';

function formatVariant(line) {
  const bits = [];
  if (line.size) bits.push(`Size ${line.size}`);
  if (line.colour) bits.push(line.colour);
  return bits.join(' · ');
}

export default function CartDrawer() {
  const navigate = useNavigate();
  const { drawerOpen, closeDrawer, lines, totals, updateQty, removeItem } = useCart();

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeDrawer}
        aria-hidden={!drawerOpen}
        className={`fixed inset-0 z-[100] transition-opacity duration-300 ${drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(75,24,56,0.45)', backdropFilter: 'blur(3px)' }}
      />
      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Shopping bag"
        aria-hidden={!drawerOpen}
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[110] h-full w-[92vw] max-w-[440px] flex flex-col shadow-2xl transition-transform duration-300 ease-out ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ background: 'var(--heritage-cream)', borderLeft: '3px solid var(--heritage-plum)' }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} style={{ color: 'var(--heritage-plum)' }} />
            <div className="h-serif text-lg tracking-[0.2em]" style={{ color: 'var(--heritage-plum)' }}>YOUR BAG</div>
          </div>
          <button onClick={closeDrawer} className="p-1 -mr-1" aria-label="Close bag" data-testid="cart-close" style={{ color: 'var(--heritage-plum)' }}>
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="cart-lines">
          {lines.length === 0 && (
            <div className="text-center py-16">
              <ShoppingBag size={40} className="mx-auto opacity-40" style={{ color: 'var(--heritage-plum)' }} />
              <div className="h-serif text-lg mt-3" style={{ color: 'var(--heritage-plum)' }}>Your bag is empty</div>
              <div className="text-xs opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>Add a Heritage piece to get started.</div>
              <button className="h-btn mt-5 text-xs" onClick={() => { closeDrawer(); navigate('/store'); }} data-testid="cart-empty-shop">Browse the store</button>
            </div>
          )}

          {lines.map((l) => (
            <div
              key={l.sku}
              className="flex gap-3 py-4 border-b"
              style={{ borderColor: 'rgba(75,24,56,0.12)' }}
              data-testid={`cart-line-${l.sku}`}
            >
              <img
                src={l.image_url}
                alt={l.name}
                className="w-20 h-24 object-cover rounded-md flex-shrink-0"
                style={{ background: '#FBF8F1' }}
              />
              <div className="flex-1 min-w-0">
                <div className="h-serif text-base leading-tight" style={{ color: 'var(--heritage-plum)' }}>{l.name}</div>
                {formatVariant(l) && (
                  <div className="text-xs opacity-80 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{formatVariant(l)}</div>
                )}
                <div className="text-sm mt-1" style={{ color: 'var(--heritage-plum)' }}>${l.unit_price.toFixed(2)}</div>

                <div className="flex items-center justify-between mt-2">
                  <div className="inline-flex items-center rounded-md border" style={{ borderColor: 'rgba(75,24,56,0.25)' }}>
                    <button
                      type="button"
                      className="p-1.5 hover:bg-[rgba(75,24,56,0.05)]"
                      onClick={() => updateQty(l.sku, l.qty - 1)}
                      disabled={l.qty <= 1}
                      aria-label="Decrease quantity"
                      data-testid={`cart-qty-dec-${l.sku}`}
                      style={{ color: 'var(--heritage-plum)', opacity: l.qty <= 1 ? 0.35 : 1 }}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-3 text-sm min-w-[2ch] text-center" style={{ color: 'var(--heritage-plum)' }} data-testid={`cart-qty-${l.sku}`}>{l.qty}</span>
                    <button
                      type="button"
                      className="p-1.5 hover:bg-[rgba(75,24,56,0.05)]"
                      onClick={() => updateQty(l.sku, l.qty + 1)}
                      disabled={l.qty >= (l.stock || 20)}
                      aria-label="Increase quantity"
                      data-testid={`cart-qty-inc-${l.sku}`}
                      style={{ color: 'var(--heritage-plum)', opacity: l.qty >= (l.stock || 20) ? 0.35 : 1 }}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                    onClick={() => removeItem(l.sku)}
                    data-testid={`cart-remove-${l.sku}`}
                    style={{ color: 'var(--heritage-plum)' }}
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {lines.length > 0 && (
          <div className="border-t px-5 py-4 space-y-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
            <div className="flex justify-between text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">${totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <span>Shipping {totals.subtotal >= 200 && '(free over $200)'}</span>
              <span data-testid="cart-shipping">${totals.shipping.toFixed(2)}</span>
            </div>
            <div className="h-divider my-2" />
            <div className="flex justify-between h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>
              <span>Total</span>
              <span data-testid="cart-total">${totals.total.toFixed(2)} NZD</span>
            </div>
            <button
              type="button"
              className="h-btn w-full mt-3"
              onClick={() => { closeDrawer(); navigate('/checkout'); }}
              data-testid="cart-checkout"
            >
              Checkout
            </button>
            <button
              type="button"
              className="h-btn-outline w-full text-xs"
              onClick={closeDrawer}
              data-testid="cart-continue-shopping"
            >
              Keep shopping
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
